
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Printer, CheckSquare, FileEdit } from "lucide-react";
import { AgreementGenerator } from "./AgreementGenerator";
import ChecklistLetter from "./ChecklistLetter";
import CustomAgreementGenerator from "./CustomAgreementGenerator";
import { EstoppelCertificate } from "./EstoppelCertificate";
import { LandContractGenerator } from "./LandContractGenerator";
import { NoteAndDeedOfTrustGenerator } from "./NoteAndDeedOfTrustGenerator";
import { QuitClaimDeedGenerator } from "./QuitClaimDeedGenerator";
import { AssignmentGenerator } from "./AssignmentGenerator";
import { BrokerAgreementGenerator } from "./BrokerAgreementGenerator";
import { useState, createContext, useContext, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// Shared state types
interface AgreementData {
  agreementType: "partial" | "full" | "private";
  date: Date | undefined;
  primarySellerName: string;
  secondarySellerName: string;
  brokerCompanyName: string;
  propertyAddress: string;
  paperType: string;
  balance: string;
  brokerOfferToSeller: string;
  firstPaymentDate: string;
  dateOfBalance: string;
  partialDollarAmount?: string;
  partialEndDate?: string;
  originalDownPayment?: string;
  recipientEmail?: string;
  paymentsRemaining?: string;
  // Private investor specific fields
  buyerName?: string;
  purchaserName?: string;
  contractDate?: string;
  interestRate?: string;
  nextPaymentDue?: string;
  purchaseAmount?: string;
  liquidatedDamages?: string;
  draftedBy?: string;
  referenceNumber?: string;
}

interface ChecklistItem {
  id: string;
  text: string;
}

interface ChecklistLetterData {
  recipientName: string;
  recipientEmail: string;
  recipientAddress: string;
  checklistItems: ChecklistItem[];
}

interface SharedDataContextType {
  agreementData: AgreementData;
  setAgreementData: (data: AgreementData | ((prev: AgreementData) => AgreementData)) => void;
  checklistData: ChecklistLetterData;
  setChecklistData: (data: ChecklistLetterData | ((prev: ChecklistLetterData) => ChecklistLetterData)) => void;
}

// Create context for shared data
export const SharedDataContext = createContext<SharedDataContextType | undefined>(undefined);

export const useSharedData = () => {
  const context = useContext(SharedDataContext);
  if (!context) {
    throw new Error('useSharedData must be used within a SharedDataProvider');
  }
  return context;
};

export function AgreementsPage() {
  const [activeTab, setActiveTab] = useState<'agreements' | 'checklist' | 'custom' | 'estoppel' | 'investor' | 'broker'>('agreements');
  const [deals, setDeals] = useState<any[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  
  // Shared state that persists across tab switches with lazy initialization
  const [agreementData, setAgreementData] = useState<AgreementData>(() => {
    try {
      const saved = localStorage.getItem('agreementData');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load saved agreement data:', e);
    }
    return {
      agreementType: "partial",
      date: undefined,
      primarySellerName: "",
      secondarySellerName: "",
      brokerCompanyName: "Contract Funding Group, LLC",
      propertyAddress: "",
      paperType: "land contract",
      balance: "",
      brokerOfferToSeller: "",
      firstPaymentDate: "",
      dateOfBalance: "",
      partialDollarAmount: "",
      partialEndDate: "",
      originalDownPayment: "",
      recipientEmail: "",
      paymentsRemaining: "",
      buyerName: "",
      purchaserName: "",
      contractDate: "",
      interestRate: "",
      nextPaymentDue: "",
      purchaseAmount: "",
      liquidatedDamages: "500.00",
      draftedBy: "",
      referenceNumber: "",
    };
  });

  const [checklistData, setChecklistData] = useState<ChecklistLetterData>(() => {
    try {
      const saved = localStorage.getItem('checklistLetterData');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load saved checklist data:', e);
    }
    return {
      recipientName: '',
      recipientEmail: '',
      recipientAddress: '',
      checklistItems: []
    };
  });

  // Fetch deals on mount
  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('id, deal_title, offer_price, property_address, seller_name, seller_email')
      .order('deal_title');
    
    if (error) {
      console.error('Error fetching deals:', error);
      return;
    }
    
    setDeals(data || []);
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const deal = deals.find(d => d.id === dealId);
    
    if (deal) {
      setAgreementData(prev => ({
        ...prev,
        brokerOfferToSeller: deal.offer_price?.toString() || "",
        propertyAddress: deal.property_address || "",
        primarySellerName: deal.seller_name || "",
        recipientEmail: deal.seller_email || "",
      }));
    }
  };

  // Save to localStorage whenever data changes
  useEffect(() => {
    try {
      localStorage.setItem('agreementData', JSON.stringify(agreementData));
    } catch (e) {
      console.error('Failed to save agreement data:', e);
    }
  }, [agreementData]);

  useEffect(() => {
    try {
      localStorage.setItem('checklistLetterData', JSON.stringify(checklistData));
    } catch (e) {
      console.error('Failed to save checklist data:', e);
    }
  }, [checklistData]);

  const sharedDataValue = {
    agreementData,
    setAgreementData,
    checklistData,
    setChecklistData
  };

  return (
    <SharedDataContext.Provider value={sharedDataValue}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Agreements & Documents</h1>
            <p className="text-muted-foreground">Generate agreements and checklist letters for customers</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            variant={activeTab === 'agreements' ? 'default' : 'outline'}
            onClick={() => setActiveTab('agreements')}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Purchase Agreements
          </Button>
          <Button 
            variant={activeTab === 'checklist' ? 'default' : 'outline'}
            onClick={() => setActiveTab('checklist')}
            className="flex items-center gap-2"
          >
            <CheckSquare className="h-4 w-4" />
            Checklist Letters
          </Button>
          <Button 
            variant={activeTab === 'custom' ? 'default' : 'outline'}
            onClick={() => setActiveTab('custom')}
            className="flex items-center gap-2"
          >
            <FileEdit className="h-4 w-4" />
            Custom Agreement
          </Button>
            <Button
              variant={activeTab === 'estoppel' ? 'default' : 'outline'}
              onClick={() => setActiveTab('estoppel')}
              className="flex items-center gap-2"
            >
              <CheckSquare className="h-4 w-4" />
              Estoppel Certificate
            </Button>
            <Button
              variant={activeTab === 'investor' ? 'default' : 'outline'}
              onClick={() => setActiveTab('investor')}
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Investor Documents
            </Button>
            <Button
              variant={activeTab === 'broker' ? 'default' : 'outline'}
              onClick={() => setActiveTab('broker')}
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Broker Agreement
            </Button>
          </div>

        {activeTab === 'agreements' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  Agreement Generator & Printing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <Label htmlFor="deal-selector">Load Deal Data (Optional)</Label>
                  <Select value={selectedDealId} onValueChange={handleDealSelect}>
                    <SelectTrigger id="deal-selector">
                      <SelectValue placeholder="Select a deal to auto-fill offer price..." />
                    </SelectTrigger>
                    <SelectContent>
                      {deals.map((deal) => (
                        <SelectItem key={deal.id} value={deal.id}>
                          {deal.deal_title} {deal.offer_price ? `- $${deal.offer_price.toLocaleString()}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select a deal to automatically fill the offer price from your pricing sheet
                  </p>
                </div>
                <p className="text-muted-foreground mb-4">
                  Create partial, full, and private investor purchase agreements. Fill in the customer and property details, 
                  then use the print function to generate a professional agreement document.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                  <h4 className="font-semibold text-amber-800 mb-2">Agreement Types Available:</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• <strong>Partial Purchase Agreement</strong> - For purchasing specific payment amounts over a defined period</li>
                    <li>• <strong>Full Purchase Agreement</strong> - For purchasing the entire remaining contract balance</li>
                    <li>• <strong>Private Investor Agreement</strong> - For private investor transactions with detailed covenants and warranties</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <AgreementGenerator />
          </>
        )}

        {activeTab === 'checklist' && <ChecklistLetter />}
        
        {activeTab === 'custom' && <CustomAgreementGenerator />}
        
        {activeTab === 'estoppel' && <EstoppelCertificate />}
        
        {activeTab === 'investor' && (
          <div className="space-y-6">
            <LandContractGenerator />
            <NoteAndDeedOfTrustGenerator />
            <QuitClaimDeedGenerator />
            <AssignmentGenerator />
          </div>
        )}

        {activeTab === 'broker' && <BrokerAgreementGenerator />}
      </div>
    </SharedDataContext.Provider>
  );
}
